import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useCallback, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { MerlinCodeChange, MerlinRequest, MerlinSSEEvent } from 'types/edgeFunctions';

interface UseMerlinStreamOptions {
  onText?: (content: string) => void;
  onCode?: (codeChange: MerlinCodeChange) => void;
  onCodeStart?: (tab: string) => void;
  onDone?: (tokensUsed: number, remainingTokens: number) => void;
  onError?: (message: string) => void;
  onCancel?: () => void;
}

type SSECallbacks = {
  onText: React.MutableRefObject<UseMerlinStreamOptions['onText']>;
  onCode: React.MutableRefObject<UseMerlinStreamOptions['onCode']>;
  onCodeStart: React.MutableRefObject<UseMerlinStreamOptions['onCodeStart']>;
  onDone: React.MutableRefObject<UseMerlinStreamOptions['onDone']>;
  onError: React.MutableRefObject<UseMerlinStreamOptions['onError']>;
  onCancel: React.MutableRefObject<UseMerlinStreamOptions['onCancel']>;
};

const processSSELines = (lines: string[], callbacks: SSECallbacks) => {
  let currentEvent = '';

  for (const line of lines) {
    if (line.startsWith('event: ')) {
      currentEvent = line.slice(7).trim();
    } else if (line.startsWith('data: ') && currentEvent) {
      try {
        const data = JSON.parse(line.slice(6)) as MerlinSSEEvent;

        switch (data.type) {
          case 'text':
            callbacks.onText.current?.(data.content);
            break;
          case 'code':
            callbacks.onCode.current?.({
              tab: data.tab,
              action: data.action,
              content: data.content,
            });
            break;
          case 'code_start':
            callbacks.onCodeStart.current?.(data.tab);
            break;
          case 'done':
            callbacks.onDone.current?.(data.tokensUsed, data.remainingTokens);
            break;
          case 'error':
            callbacks.onError.current?.(data.message);
            break;
        }
      } catch {
        // Skip malformed JSON
      }
      currentEvent = '';
    }
  }
};

export const useMerlinStream = (options: UseMerlinStreamOptions) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const supabase = useSupabaseTypedClient();
  const { isSuperUser } = useCurrentUser();

  // Store callbacks in refs to avoid stale closures and prevent
  // sendMessage from being recreated on every render
  const onTextRef = useRef(options.onText);
  const onCodeRef = useRef(options.onCode);
  const onCodeStartRef = useRef(options.onCodeStart);
  const onDoneRef = useRef(options.onDone);
  const onErrorRef = useRef(options.onError);
  const onCancelRef = useRef(options.onCancel);
  onTextRef.current = options.onText;
  onCodeRef.current = options.onCode;
  onCodeStartRef.current = options.onCodeStart;
  onDoneRef.current = options.onDone;
  onErrorRef.current = options.onError;
  onCancelRef.current = options.onCancel;

  const sendMessage = useCallback(
    async (request: MerlinRequest) => {
      // Get current session for auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        onErrorRef.current?.('Not authenticated');
        return;
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const url = `${supabaseUrl}/functions/v1/ask-merlin-ai`;

      abortControllerRef.current = new AbortController();
      setIsStreaming(true);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(request),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = 'Something went wrong';

          if (response.status === 403) {
            errorMessage = 'Upgrade your plan to use Merlin AI.';
          } else if (response.status === 429) {
            errorMessage = 'AI usage limit reached. Use BigTokens to extend your limit.';
          } else {
            try {
              const errorJson = JSON.parse(errorText);
              errorMessage = errorJson.message || errorMessage;
            } catch {
              // Use default error message
            }
          }

          onErrorRef.current?.(errorMessage);
          setIsStreaming(false);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          onErrorRef.current?.('No response stream');
          setIsStreaming(false);
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';
        const callbacks: SSECallbacks = { onText: onTextRef, onCode: onCodeRef, onCodeStart: onCodeStartRef, onDone: onDoneRef, onError: onErrorRef, onCancel: onCancelRef };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          processSSELines(lines, callbacks);
        }

        // Flush remaining buffer after stream ends — prevents losing
        // trailing events (e.g. the 'done' event) when the last chunk
        // doesn't end with a newline
        buffer += decoder.decode(new Uint8Array(), { stream: false });
        if (buffer.trim()) {
          const remainingLines = buffer.split('\n');
          processSSELines(remainingLines, callbacks);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          onCancelRef.current?.();
        } else {
          onErrorRef.current?.(err instanceof Error ? err.message : 'Stream connection failed');
        }
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supabase]
  );

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
  }, []);

  return { sendMessage, cancelStream, isStreaming };
};
